import React, { useState } from 'react';
import { VulnerabilityDetail } from '../../../types/interfaces';
import { useTranslation } from 'react-i18next';
import {
  FaExclamationTriangle,
  FaExclamationCircle,
  FaCheckCircle,
  FaQuestionCircle,
} from 'react-icons/fa';

interface VulnerabilitiesProps {
  vulnerabilities?: VulnerabilityDetail[];
}

/** Ordena las vulnerabilidades por criticidad (CVSS de mayor a menor). */
function sortByCriticity(vuls: VulnerabilityDetail[]): VulnerabilityDetail[] {
  return [...vuls].sort((a, b) => (b.cvss ?? 0) - (a.cvss ?? 0));
}

/** Retorna la clave de severidad (critical, high, medium, low, unknown). */
function getSeverityKey(cvss?: number | null): string {
  if (cvss == null) return 'unknown';
  if (cvss >= 9.0) return 'critical';
  if (cvss >= 7.0) return 'high';
  if (cvss >= 4.0) return 'medium';
  return 'low';
}

/** Devuelve el icono correspondiente y su color, según la criticidad. */
function getSeverityIcon(cvss?: number | null) {
  if (cvss == null) {
    return <FaQuestionCircle className="text-gray-400" />;
  } else if (cvss >= 9.0) {
    return <FaExclamationTriangle className="text-red-600" />;
  } else if (cvss >= 7.0) {
    return <FaExclamationTriangle className="text-orange-500" />;
  } else if (cvss >= 4.0) {
    return <FaExclamationCircle className="text-yellow-400" />;
  } else {
    return <FaCheckCircle className="text-green-500" />;
  }
}

const Vulnerabilities: React.FC<VulnerabilitiesProps> = ({ vulnerabilities }) => {
  const { t } = useTranslation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  if (!vulnerabilities || vulnerabilities.length === 0) {
    return (
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h3 className="text-4xl font-bold mb-4">{t('vulnerabilities.title')}</h3>
        <p>{t('vulnerabilities.no_vulnerabilities')}</p>
      </div>
    );
  }

  const orderedVulnerabilities = sortByCriticity(vulnerabilities);

  return (
    <div className="relative">
      {/* Encabezado: mensaje y botón para abrir el sidebar */}
      <div className="flex items-center justify-between p-4 bg-gray-100 rounded-lg mb-4">
        <p className="text-xl text-gray-700">
          {t('vulnerabilities.found', { count: vulnerabilities.length })}
        </p>
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="bg-gray-800 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-300"
        >
          {t('vulnerabilities.show')}
        </button>
      </div>

      {/* Overlay y Sidebar */}
      {isSidebarOpen && (
        <>
          {/* Overlay semitransparente */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setIsSidebarOpen(false)}
          ></div>

          {/* Sidebar con ancho mayor */}
          <div className="fixed top-0 right-0 h-screen w-[640px] bg-white shadow-xl z-50 p-6 overflow-y-auto">
            {/* Botón de cierre ubicado a la izquierda */}
            <div className="flex justify-start mb-4">
              <button
                onClick={() => setIsSidebarOpen(false)}
                className="bg-gray-800 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors duration-300"
              >
                ← {t('vulnerabilities.back')}
              </button>
            </div>

            {/* Lista de vulnerabilidades */}
            <div className="space-y-6">
              {orderedVulnerabilities.map((vuln, index) => {
                const severityKey = getSeverityKey(vuln.cvss);
                const icon = getSeverityIcon(vuln.cvss);

                return (
                  <div key={index} className="border rounded-lg shadow-md">
                    {/* Encabezado con CVE */}
                    <h4 className="bg-gray-800 text-white p-4 font-semibold text-xl text-center">
                      {vuln.cve}
                    </h4>
                    <table className="table-auto w-full border-collapse">
                      <tbody>
                        {/* Severidad */}
                        <tr className="border-b bg-gray-50">
                          <td className="bg-gray-800 p-3 font-semibold text-white text-center">
                            {t('vulnerabilities.table.severity')}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 text-center">
                            <div className="flex items-center justify-center space-x-2">
                              {icon}
                              <span>
                                {vuln.cvss !== null
                                  ? `${vuln.cvss} - ${t(
                                      `vulnerabilities.severity.${severityKey}`
                                    )}`
                                  : t('vulnerabilities.unknown')}
                              </span>
                            </div>
                          </td>
                        </tr>

                        {/* Descripción */}
                        <tr className="border-b bg-gray-50">
                          <td className="bg-gray-800 p-3 font-semibold text-white text-center">
                            {t('vulnerabilities.table.description')}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3" style={{ textAlign: 'justify' }}>
                            {vuln.description || t('vulnerabilities.no_description')}
                          </td>
                        </tr>

                        {/* Referencias */}
                        <tr className="border-b bg-gray-50">
                          <td className="bg-gray-800 p-3 font-semibold text-white text-center">
                            {t('vulnerabilities.table.references')}
                          </td>
                        </tr>
                        <tr className="border-b">
                          <td className="p-3 text-center">
                            <a
                              href={`https://nvd.nist.gov/vuln/detail/${vuln.cve}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-500 underline"
                            >
                              {`https://nvd.nist.gov/vuln/detail/${vuln.cve}`}
                            </a>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Vulnerabilities;
